import {
  validatePhase8Report,
  phase8SessionFilename,
  PHASE8_SESSIONS
} from './phase8Scorecard';

/**
 * Boot / runtime telemetry — Phase 0 + Phase 8 (ADR-0014 verification).
 * Opt-in only: REACT_APP_BOOT_TELEMETRY=1 (never auto-on in development —
 * hot-path IDB/KV/JSON/render counters overload interactive sessions).
 *
 * Phase 8 adds:
 * - ADR gate aliases: shell_ready, surface_data_ready, transaction_ready
 * - Surface mounts: manager_mounted, stock_mounted, inventory_mounted
 * - Operation spans: sale_commit / purchase_commit
 * Phase 8.3: validate() / downloadSession() scorecard against protocol.
 */

const GATE_MARKS = [
  'react_bootstrap_start',
  'electron_storage_probe_done',
  'auth_effect_start',
  'storage_engine_start',
  'storage_idb_open_done',
  'storage_cache_hydrate_done',
  'storage_engine_ready',
  'auth_session_restore_done',
  'machine_binding_start',
  'machine_binding_done',
  'auth_loading_done',
  'role_context_start',
  'role_context_ready',
  'role_maintenance_start',
  'role_maintenance_done',
  'business_load_start',
  'business_blob_read_done',
  'business_blob_read_skipped',
  'data_shell_ready',
  'shell_ready',
  'entity_hydrate_deferred',
  'entity_hydrate_awaited',
  'payload_consolidation_deferred',
  'aggregate_index_backfill_deferred',
  'business_phase1_done',
  'surface_data_ready',
  'business_phase2_done',
  'instant_cache_boot_done',
  'route_gate_passed',
  'dashboard_mounted',
  'dashboard_interactive',
  'manager_mounted',
  'stock_mounted',
  'inventory_mounted',
  'pos_data_ready',
  'transaction_ready',
  'sale_commit_start',
  'sale_commit_done',
  'purchase_commit_start',
  'purchase_commit_done'
];

/** ADR-0014 aliases emitted alongside legacy gate names (no behavior change). */
const GATE_ALIASES = Object.freeze({
  data_shell_ready: ['shell_ready'],
  business_phase1_done: ['surface_data_ready']
});

const state = {
  enabled: false,
  originMs: typeof performance !== 'undefined' ? performance.timeOrigin : Date.now(),
  marks: new Map(),
  measures: [],
  counters: {
    idbReads: 0,
    idbWrites: 0,
    kvGets: 0,
    kvSets: 0,
    jsonParseCalls: 0,
    jsonParseBytes: 0,
    jsonStringifyCalls: 0,
    jsonStringifyBytes: 0,
    reactRenders: 0,
    checkoutBlocked: 0,
    saleCommits: 0,
    purchaseCommits: 0
  },
  meta: {},
  renderTimings: [],
  operations: []
};

function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now() - state.originMs;
}

function estimateBytes(value) {
  if (value == null) return 0;
  if (typeof value === 'string') return value.length;
  try {
    return JSON.stringify(value).length;
  } catch {
    return 0;
  }
}

function computeBootTelemetryEnabled() {
  if (typeof window === 'undefined') return false;
  const flag = process.env.REACT_APP_BOOT_TELEMETRY;
  return flag === '1' || flag === 'true';
}

/** Fast path after init — hot KV/IDB callers must not re-parse env each time. */
export function isBootTelemetryEnabled() {
  return state.enabled;
}

export function initBootTelemetry() {
  state.enabled = computeBootTelemetryEnabled();
  if (!state.enabled || typeof window === 'undefined') {
    if (typeof window !== 'undefined') {
      window.__AMAN_BOOT_TELEMETRY = {
        isEnabled: () => false,
        export: () => ({ enabled: false, reason: 'set REACT_APP_BOOT_TELEMETRY=1 to enable' }),
        validate: () => ({ ok: false, session: null, checks: [], enabled: false }),
        download: () => null,
        downloadSession: () => null
      };
    }
    return;
  }

  state.marks.clear();
  state.measures = [];
  state.operations = [];
  state.renderTimings = [];
  state.meta = {};
  state.counters = {
    idbReads: 0,
    idbWrites: 0,
    kvGets: 0,
    kvSets: 0,
    jsonParseCalls: 0,
    jsonParseBytes: 0,
    jsonStringifyCalls: 0,
    jsonStringifyBytes: 0,
    reactRenders: 0,
    checkoutBlocked: 0,
    saleCommits: 0,
    purchaseCommits: 0
  };

  window.__AMAN_BOOT_TELEMETRY = bootTelemetryApi;
  markBootGate('react_bootstrap_start');
}

function markBootGateOnce(name, detail = null) {
  if (!state.enabled) return;
  if (state.marks.has(name)) return;
  const t = nowMs();
  state.marks.set(name, { t, detail });
  try {
    performance.mark(`aman:boot:${name}`);
  } catch {
    /* ignore */
  }
}

export function markBootGate(name, detail = null) {
  if (!state.enabled) return;
  const t = nowMs();
  state.marks.set(name, { t, detail });
  try {
    performance.mark(`aman:boot:${name}`);
  } catch {
    /* ignore */
  }
  const aliases = GATE_ALIASES[name];
  if (aliases) {
    for (const alias of aliases) {
      markBootGateOnce(alias, detail);
    }
  }
}

/**
 * First-paint mount mark for a surface (Phase 8). Idempotent per session name.
 * @param {'manager'|'stock'|'inventory'|'pos'} surface
 */
export function markSurfaceMounted(surface) {
  if (!state.enabled || !surface) return;
  const gate = `${surface}_mounted`;
  if (state.marks.has(gate)) return;
  markBootGate(gate, { surface });
  measureBoot('react_bootstrap_start', gate, `bootstrap_to_${surface}`);
}

/**
 * ADR-0014 transactionReady — call when POS may sell.
 */
export function markTransactionReady(detail = null) {
  if (!state.enabled) return;
  if (state.marks.has('transaction_ready')) return;
  markBootGate('transaction_ready', detail);
  measureBoot('dashboard_mounted', 'transaction_ready', 'dashboard_to_transaction');
  measureBoot('react_bootstrap_start', 'transaction_ready', 'bootstrap_to_transaction');
}

export function setBootMeta(key, value) {
  if (!state.enabled) return;
  state.meta[key] = value;
}

export function measureBoot(fromMark, toMark, label = null) {
  if (!state.enabled) return null;
  const from = state.marks.get(fromMark);
  const to = state.marks.get(toMark);
  if (!from || !to) return null;
  const durationMs = Math.round(to.t - from.t);
  const entry = { label: label || `${fromMark}→${toMark}`, durationMs, fromMark, toMark };
  state.measures.push(entry);
  try {
    performance.measure(`aman:boot:${entry.label}`, `aman:boot:${fromMark}`, `aman:boot:${toMark}`);
  } catch {
    /* marks may be missing in performance timeline */
  }
  return entry;
}

/**
 * Sync operation span (sale_commit / purchase_commit).
 * @param {string} name
 * @param {() => unknown} fn
 */
export function measureSyncOperation(name, fn) {
  if (!state.enabled) {
    return typeof fn === 'function' ? fn() : undefined;
  }
  markBootGate(`${name}_start`);
  try {
    return typeof fn === 'function' ? fn() : undefined;
  } finally {
    markBootGate(`${name}_done`);
    const measured = measureBoot(`${name}_start`, `${name}_done`, name);
    if (measured) {
      state.operations.push({ name, durationMs: measured.durationMs, at: nowMs() });
      if (state.operations.length > 40) state.operations.shift();
    }
    if (name === 'sale_commit') state.counters.saleCommits += 1;
    if (name === 'purchase_commit') state.counters.purchaseCommits += 1;
  }
}

export function recordKvGet(key) {
  if (!state.enabled) return;
  state.counters.kvGets += 1;
  if (key && String(key).includes('business_') && String(key).includes('_data')) {
    setBootMeta('lastBusinessBlobGetKey', key);
  }
}

export function recordKvSet(key) {
  if (!state.enabled) return;
  state.counters.kvSets += 1;
}

export function recordIdbRead(key = null, byteLength = 0) {
  if (!state.enabled) return;
  state.counters.idbReads += 1;
  if (byteLength > 0) {
    state.counters.jsonParseBytes += byteLength;
  }
  if (key) {
    setBootMeta('lastIdbReadKey', key);
  }
}

export function recordIdbWrite(key = null) {
  if (!state.enabled) return;
  state.counters.idbWrites += 1;
  if (key) {
    setBootMeta('lastIdbWriteKey', key);
  }
}

export function recordJsonParse(source = 'unknown', rawLength = 0) {
  if (!state.enabled) return;
  state.counters.jsonParseCalls += 1;
  state.counters.jsonParseBytes += rawLength || 0;
  setBootMeta('lastJsonParseSource', source);
}

export function recordJsonStringify(source = 'unknown', rawLength = 0) {
  if (!state.enabled) return;
  state.counters.jsonStringifyCalls += 1;
  state.counters.jsonStringifyBytes += rawLength || 0;
  setBootMeta('lastJsonStringifySource', source);
}

export function recordReactRender(componentName, phase = 'mount') {
  if (!state.enabled) return;
  state.counters.reactRenders += 1;
  const t = nowMs();
  state.renderTimings.push({ componentName, phase, t });
  if (state.renderTimings.length > 50) {
    state.renderTimings.shift();
  }
}

export function recordCheckoutBlocked() {
  if (!state.enabled) return;
  state.counters.checkoutBlocked += 1;
}

export function tracedJsonParse(raw, source = 'JsonSerializer') {
  if (state.enabled && raw != null) {
    recordJsonParse(source, typeof raw === 'string' ? raw.length : estimateBytes(raw));
  }
  return JSON.parse(raw);
}

export function tracedJsonStringify(value, source = 'JsonSerializer') {
  const out = JSON.stringify(value);
  if (state.enabled) {
    recordJsonStringify(source, out.length);
  }
  return out;
}

function msSinceBootstrap(markName) {
  const start = state.marks.get('react_bootstrap_start')?.t ?? 0;
  const mark = state.marks.get(markName)?.t;
  return mark != null ? Math.round(mark - start) : null;
}

function buildTimeline() {
  const start = state.marks.get('react_bootstrap_start')?.t ?? 0;
  return GATE_MARKS.filter((name) => state.marks.has(name)).map((name) => {
    const entry = state.marks.get(name);
    return {
      gate: name,
      msSinceOrigin: Math.round(entry.t),
      msSinceBootstrap: Math.round(entry.t - start),
      detail: entry.detail ?? null
    };
  });
}

function buildReport() {
  const timeline = buildTimeline();
  const start = state.marks.get('react_bootstrap_start')?.t ?? 0;
  const dashboardMount = state.marks.get('dashboard_mounted')?.t;
  const phase1 = state.marks.get('business_phase1_done')?.t;

  return {
    enabled: state.enabled,
    generatedAt: new Date().toISOString(),
    phase: 'adr-0014-phase-8',
    summary: {
      msToDashboardMount:
        dashboardMount != null ? Math.round(dashboardMount - start) : null,
      msToPhase1Done: phase1 != null ? Math.round(phase1 - start) : null,
      msToShellReady: msSinceBootstrap('shell_ready') ?? msSinceBootstrap('data_shell_ready'),
      msToSurfaceDataReady:
        msSinceBootstrap('surface_data_ready') ?? msSinceBootstrap('business_phase1_done'),
      msToTransactionReady: msSinceBootstrap('transaction_ready'),
      msToManagerMount: msSinceBootstrap('manager_mounted'),
      msToStockMount: msSinceBootstrap('stock_mounted'),
      msToInventoryMount: msSinceBootstrap('inventory_mounted'),
      ...state.counters
    },
    adr0014Gates: {
      shellReady: state.marks.has('shell_ready') || state.marks.has('data_shell_ready'),
      surfaceDataReady:
        state.marks.has('surface_data_ready') || state.marks.has('business_phase1_done'),
      transactionReady: state.marks.has('transaction_ready')
    },
    meta: { ...state.meta },
    timeline,
    measures: [...state.measures],
    operations: [...state.operations],
    renderTimings: [...state.renderTimings]
  };
}

function downloadReport(filename = null) {
  if (typeof document === 'undefined') return buildReport();
  const report = buildReport();
  const name =
    filename ||
    `boot-baseline-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
  return report;
}

/**
 * Phase 8.3 — scorecard against current export (or provided report).
 * @param {string} [session] A|B|C|D|E|all
 * @param {object} [report] optional pre-built report
 */
function validateSession(session = 'A', report = null) {
  return validatePhase8Report(report || buildReport(), session);
}

/**
 * Download with Protocol filename for the session (p8-boot-pos-… etc.).
 * @param {string} [session]
 */
function downloadSession(session = 'A') {
  const name = phase8SessionFilename(session);
  return downloadReport(name);
}

const bootTelemetryApi = {
  mark: markBootGate,
  markSurface: markSurfaceMounted,
  markTransactionReady,
  measureSyncOperation,
  setMeta: setBootMeta,
  measure: measureBoot,
  export: buildReport,
  download: downloadReport,
  validate: validateSession,
  downloadSession,
  isEnabled: () => state.enabled,
  GATE_MARKS,
  GATE_ALIASES,
  PHASE8_SESSIONS
};

export { validatePhase8Report, phase8SessionFilename };

export default bootTelemetryApi;
